/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1b §6a] THE IDEMPOTENCY KEY FOR ONE REFRESH-TOKEN ROTATION.
 *
 *  The client mints it, sends it with the rotation, and keeps it until the answer lands. If
 *  the answer never lands — a timeout, a reload, an OS app-kill on a handset with no signal —
 *  the next attempt presents the SAME key and the server re-serves the SAME successor rather
 *  than reading a spent token as a replay and killing the family.
 *
 *  ⚠️ **NEVER DERIVED FROM THE TOKEN.** `key = f(parentToken)` needs no storage at all and is
 *  the tempting answer. It is also the grace window under a different name: anyone holding
 *  the parent can compute the key, so reuse detection on a replayed parent stops existing.
 *  Not a trade-off — a repeal.
 *
 *  ⚠️ **`crypto.getRandomValues`, NEVER `crypto.randomUUID`.** Divergence #28: `randomUUID` is
 *  secure-context-only and is `undefined` over plain `http://` to a LAN address, which is this
 *  product's documented demo case — and Node and jsdom expose it unconditionally, so no test
 *  in this repository can see the difference.
 *
 *  ⚠️ **AND THERE IS NO `Math.random` FALLBACK, UNLIKE `refreshLock.ts`'s HOLDER ID.** That
 *  file's own comment says why its fallback is acceptable "HERE and nowhere else: a holder-id
 *  collision costs one extra rotation and never a credential". A GUESSABLE rotation key is a
 *  bypass of the immediate reuse detection, so this returns `undefined` instead and the client
 *  sends no key — behaving exactly as it did before this file existed. Fail closed.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const HEX = '0123456789abcdef';

export const mintRotationKey = (): string | undefined => {
  const source = globalThis.crypto;
  if (!source?.getRandomValues) return undefined;

  /* 16 bytes — `requestId`'s entropy, for `requestId`'s reason. 32 hex characters clears the
     server's `{22,64}` band with room for a later encoding change. */
  const bytes = source.getRandomValues(new Uint8Array(16));
  let key = '';
  for (const byte of bytes) key += `${HEX[byte >> 4] ?? '0'}${HEX[byte & 15] ?? '0'}`;
  return key;
};
